import {
  Box,
  TableRow,
  Paper,
  TableContainer,
  CircularProgress,
  Typography,
  Table,
  TableHead,
  TableCell,
  TableBody,
  Button,
  Breadcrumbs,
  Link,
  Chip,
  LinearProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import { Link as RouterLink, useParams, useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import {
  useListNotificationCampaigns,
  useGetTargetDefinitionTypes,
  useCreateNotificationCampaign,
} from "../../../clients/graphql/pages/notification-queries";
import moment from "moment";

interface NotificationCampaign {
  notificationKey: number;
  notificationTemplateKey: number;
  targetMode: string;
  targetDefinition: JSON;
  scheduledAt: string;
  endAt: string;
  totalRecipients: number;
  sentCount: number;
  ackedCount: number;
  createdAt: string;
  subject: string;
  body: string;
  createdByUserKey: number;
}

export const NotificationCampaignsPage: React.FC = () => {
  const { templateKey, templateType } = useParams<{
    templateKey: string;
    templateType: string;
  }>();
  const navigate = useNavigate();
  const notificationTemplateKey = parseInt(templateKey!);
  const notificationTemplateType = templateType!;
  const [selectedTargetDefinition, setSelectedTargetDefinition] =
    useState<JSON | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    targetMode: "",
    targetDefinition: "{}",
    scheduledAt: "",
    endAt: "",
  });

  const { loading, error, data } = useListNotificationCampaigns(
    notificationTemplateKey
  );
  const {
    loading: targetTypesLoading,
    error: targetTypesError,
    data: targetTypesData,
  } = useGetTargetDefinitionTypes(notificationTemplateKey);
  const [createCampaign, { loading: creating, error: createError }] =
    useCreateNotificationCampaign({
      onCompleted: () => {
        // Refetch the campaigns list after creation
        window.location.reload();
      },
    });

  const formatTargetDefinition = (targetDefinition: string) => {
    try {
      const parsed = JSON.parse(targetDefinition);
      return JSON.stringify(parsed, null, 2);
    } catch (error) {
      return targetDefinition;
    }
  };

  const handleCloseTargetDialog = () => {
    setSelectedTargetDefinition(null);
  };

  const getProgressPercentage = (sent: number, total: number) => {
    return total > 0 ? Math.round((sent / total) * 100) : 0;
  };

  const getAckPercentage = (acked: number, total: number) => {
    return total > 0 ? Math.round((acked / total) * 100) : 0;
  };

  const handleCreateCampaign = async () => {
    try {
      const targetDefinition = JSON.parse(formData.targetDefinition);
      console.log(notificationTemplateType);

      const { data: result } = await createCampaign({
        variables: {
          input: {
            type: notificationTemplateType,
            targetMode: formData.targetMode,
            targetDefinition,
            scheduledAt: formData.scheduledAt,
            endAt: formData.endAt,
          },
        },
      });

      if (result?.createNotificationCampaign?.success) {
        setCreateDialogOpen(false);
        setFormData({
          targetMode: "",
          targetDefinition: "{}",
          scheduledAt: "",
          endAt: "",
        });
        // Optionally navigate to the new campaign
        if (result.createNotificationCampaign.notificationKey) {
          navigate(
            `/developer/notifications/campaigns/${result.createNotificationCampaign.notificationKey}`
          );
        }
      }
    } catch (err) {
      console.error("Error creating campaign:", err);
    }
  };

  const handleTargetModeChange = (targetMode: string) => {
    setFormData((prev) => ({ ...prev, targetMode }));

    // Use the target definition schema if available
    if (targetTypesData?.getTargetDefinitionTypes?.targetDefinitionSchema) {
      try {
        const schema = JSON.parse(
          targetTypesData.getTargetDefinitionTypes.targetDefinitionSchema
        );
        setFormData((prev) => ({
          ...prev,
          targetDefinition: JSON.stringify(schema, null, 2),
        }));
      } catch (e) {
        // Keep default if schema is invalid
      }
    }
  };

  // Set default target mode when data loads
  useEffect(() => {
    if (
      targetTypesData?.getTargetDefinitionTypes?.defaultTargetMode &&
      !formData.targetMode
    ) {
      const defaultTargetMode =
        targetTypesData.getTargetDefinitionTypes.defaultTargetMode;
      setFormData((prev) => ({
        ...prev,
        targetMode: defaultTargetMode,
      }));

      // Also set the target definition schema if available
      if (targetTypesData.getTargetDefinitionTypes.targetDefinitionSchema) {
        try {
          const schema = JSON.parse(
            targetTypesData.getTargetDefinitionTypes.targetDefinitionSchema
          );
          setFormData((prev) => ({
            ...prev,
            targetMode: defaultTargetMode,
            targetDefinition: JSON.stringify(schema, null, 2),
          }));
        } catch (e) {
          // Keep default if schema is invalid
        }
      }
    }
  }, [targetTypesData, formData.targetMode]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  console.log(data);

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          Error loading notification campaigns: {error.message}
        </Typography>
      </Box>
    );
  }

  const campaigns = data?.listNotificationCampaigns || [];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs>
          <Link component={RouterLink} to="/developer" color="inherit">
            Developer
          </Link>
          <Link
            component={RouterLink}
            to="/developer/notifications"
            color="inherit"
          >
            Notifications
          </Link>
          <Typography color="text.primary">
            Template {templateKey} Campaigns
          </Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/developer/notifications")}
          sx={{ mb: 2, p: 0, color: "text.secondary" }}
        >
          Back to Templates
        </Button>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h4">
            Notification Campaigns - Template {templateKey}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            color="primary"
          >
            Create Campaign
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Campaign ID</TableCell>
              <TableCell>Target Mode</TableCell>
              <TableCell>Scheduled At</TableCell>
              <TableCell>End At</TableCell>
              <TableCell>Sent Progress</TableCell>
              <TableCell>Acknowledgment Rate</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No campaigns found for this template
                </TableCell>
              </TableRow>
            ) : (
              campaigns.map((campaign: NotificationCampaign) => {
                const sentPercentage = getProgressPercentage(
                  campaign.sentCount,
                  campaign.totalRecipients
                );
                const ackPercentage = getAckPercentage(
                  campaign.ackedCount,
                  campaign.totalRecipients
                );

                return (
                  <TableRow
                    key={campaign.notificationKey}
                    sx={{
                      "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                    }}
                  >
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/developer/notifications/campaigns/${campaign.notificationKey}`}
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        #{campaign.notificationKey}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Chip
                          label={campaign.targetMode}
                          variant="outlined"
                          size="small"
                        />
                        <IconButton
                          size="small"
                          onClick={() =>
                            setSelectedTargetDefinition(
                              campaign.targetDefinition
                            )
                          }
                          sx={{ color: "text.secondary" }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {moment(campaign.scheduledAt).format("M/D/YYYY h:mm a")}
                    </TableCell>
                    <TableCell>
                      {moment(campaign.endAt).format("M/D/YYYY h:mm a")}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LinearProgress
                          variant="determinate"
                          value={sentPercentage}
                          sx={{ width: 60, height: 8 }}
                        />
                        <Typography variant="body2">
                          {campaign.sentCount}/{campaign.totalRecipients} (
                          {sentPercentage}%)
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LinearProgress
                          variant="determinate"
                          value={ackPercentage}
                          color="success"
                          sx={{ width: 60, height: 8 }}
                        />
                        <Typography variant="body2">
                          {campaign.ackedCount}/{campaign.totalRecipients} (
                          {ackPercentage}%)
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{campaign.createdByUserKey ?? "Autopilot"}</TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        component={RouterLink}
                        to={`/developer/notifications/campaigns/${campaign.notificationKey}`}
                      >
                        View Recipients
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Campaign Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Campaign</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Target Mode"
                value={formData.targetMode}
                onChange={(e) => handleTargetModeChange(e.target.value)}
                disabled={targetTypesLoading}
                helperText={
                  targetTypesData?.getTargetDefinitionTypes?.defaultTargetMode
                    ? `Default: ${targetTypesData.getTargetDefinitionTypes.defaultTargetMode}`
                    : "Target mode for this template"
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Target Definition (JSON)"
                multiline
                rows={8}
                value={formData.targetDefinition}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    targetDefinition: e.target.value,
                  }))
                }
                helperText="Define the targeting criteria as JSON"
                sx={{
                  "& .MuiInputBase-input": {
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Scheduled At"
                type="datetime-local"
                value={formData.scheduledAt}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    scheduledAt: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="End At"
                type="datetime-local"
                value={formData.endAt}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, endAt: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          {createError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Error creating campaign: {createError.message}
            </Alert>
          )}

          {targetTypesError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Error loading target types: {targetTypesError.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateCampaign}
            variant="contained"
            disabled={
              creating ||
              !formData.targetMode ||
              !formData.scheduledAt ||
              !formData.endAt
            }
          >
            {creating ? <CircularProgress size={20} /> : "Create Campaign"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Target Definition Dialog */}
      <Dialog
        open={!!selectedTargetDefinition}
        onClose={handleCloseTargetDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Target Definition</DialogTitle>
        <DialogContent>
          <pre
            style={{
              backgroundColor: "rgb(50, 50, 50)",
              padding: "16px",
              borderRadius: "4px",
              overflow: "auto",
              fontSize: "14px",
              fontFamily: "monospace",
            }}
          >
            {selectedTargetDefinition &&
              formatTargetDefinition(
                JSON.stringify(selectedTargetDefinition, null, 2)
              )}
          </pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTargetDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
